import { useState, useEffect, useCallback } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { Layout } from '../../components/Layout'
import { hasAnyRole } from '../../utils/permissions'
import { supabase } from '../../services/supabase'
import { Calendar, ChevronLeft, ChevronRight, Filter, Search, User, School, BarChart3, X } from 'lucide-react'

const NotificationHistory = () => {
  const { userProfile, loading: authLoading } = useAuth()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const itemsPerPage = 10

  // Filtreler
  const [selectedMonth, setSelectedMonth] = useState('')
  const [selectedDay, setSelectedDay] = useState('')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCreator, setSelectedCreator] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [creators, setCreators] = useState([])
  const [categories, setCategories] = useState([])
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [notificationStats, setNotificationStats] = useState([])
  const [statsLoading, setStatsLoading] = useState(false)
  const [statsStatusFilter, setStatsStatusFilter] = useState('all')
  const [statsSearchTerm, setStatsSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState('details') // 'details' veya 'stats'

  const statusOptions = [
    { value: 'Okundu', label: 'Okundu', color: 'blue' },
    { value: 'Okunmadı', label: 'Okunmadı', color: 'gray' },
    { value: 'İlgili Değil', label: 'İlgili Değil', color: 'yellow' },
    { value: 'Yaptım', label: 'Yaptım', color: 'green' },
    { value: 'Yapmadım', label: 'Yapmadım', color: 'red' },
    { value: 'Sorun Oluştu', label: 'Sorun Oluştu', color: 'orange' },
  ]

  const fetchCreators = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, full_name, email')
        .order('full_name')

      if (error) throw error
      setCreators(data || [])
    } catch (error) {
      console.error('Gönderenler yüklenemedi:', error)
    }
  }

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('notification_categories')
        .select('id, display_name')
        .eq('is_active', true)
        .order('display_name')

      if (error) throw error
      setCategories(data || [])
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error)
    }
  }

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true)

      // Tarih filtresi oluştur
      let dateFilter = {}
      if (selectedYear) {
        const year = parseInt(selectedYear)
        if (selectedMonth) {
          const month = parseInt(selectedMonth)
          const startDate = new Date(year, month - 1, 1).toISOString()
          const endDate = new Date(year, month, 0, 23, 59, 59).toISOString()
          if (selectedDay) {
            const day = parseInt(selectedDay)
            const startDateWithDay = new Date(year, month - 1, day).toISOString()
            const endDateWithDay = new Date(year, month - 1, day, 23, 59, 59).toISOString()
            dateFilter = { gte: startDateWithDay, lte: endDateWithDay }
          } else {
            dateFilter = { gte: startDate, lte: endDate }
          }
        } else {
          const startDate = new Date(year, 0, 1).toISOString()
          const endDate = new Date(year, 11, 31, 23, 59, 59).toISOString()
          dateFilter = { gte: startDate, lte: endDate }
        }
      }

      // Bildirimleri getir
      let query = supabase
        .from('notifications')
        .select(`
          *,
          created_by_user:users!notifications_created_by_fkey(id, full_name, email),
          targets:notification_targets(
            school_id,
            school:schools(id, okul_adi)
          ),
          status:notification_status(school_id, status, notes, updated_at)
        `, { count: 'exact' })

      // Tarih filtresi
      if (dateFilter.gte) {
        query = query.gte('created_at', dateFilter.gte)
      }
      if (dateFilter.lte) {
        query = query.lte('created_at', dateFilter.lte)
      }

      // Gönderen filtresi
      if (selectedCreator) {
        query = query.eq('created_by', selectedCreator)
      }

      // Arama filtresi
      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
      }

      // Kategori filtresi için önce kategori ilişkilerini kontrol et
      let notificationIds = null
      if (selectedCategory) {
        const { data: categoryRelations } = await supabase
          .from('notification_category_relations')
          .select('notification_id')
          .eq('category_id', selectedCategory)

        if (categoryRelations && categoryRelations.length > 0) {
          notificationIds = categoryRelations.map(r => r.notification_id)
          query = query.in('id', notificationIds)
        } else {
          // Eğer kategoriye ait bildirim yoksa boş sonuç döndür
          setNotifications([])
          setTotalCount(0)
          setTotalPages(1)
          setLoading(false)
          return
        }
      }

      // Sayfalama
      const from = (currentPage - 1) * itemsPerPage
      const to = from + itemsPerPage - 1

      query = query
        .order('created_at', { ascending: false })
        .range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      // Kategorileri ayrı sorgu ile çek
      const notificationIdsFromData = (data || []).map(n => n.id)
      let categoriesData = []
      if (notificationIdsFromData.length > 0) {
        const { data: categoryRelations } = await supabase
          .from('notification_category_relations')
          .select(`
            notification_id,
            category:notification_categories(*)
          `)
          .in('notification_id', notificationIdsFromData)

        categoriesData = categoryRelations || []
      }

      // Bildirimlere kategorileri ekle
      const notificationsWithCategories = (data || []).map(notification => ({
        ...notification,
        categories: categoriesData
          .filter(rel => rel.notification_id === notification.id)
          .map(rel => ({
            category_id: rel.category_id,
            category: rel.category
          }))
      }))

      setNotifications(notificationsWithCategories)
      setTotalCount(count || 0)
      setTotalPages(Math.ceil((count || 0) / itemsPerPage))
    } catch (error) {
      console.error('Bildirimler yüklenemedi:', error)
      alert('Bildirimler yüklenirken hata oluştu: ' + error.message)
    } finally {
      setLoading(false)
    }
  }, [currentPage, selectedMonth, selectedDay, selectedYear, searchTerm, selectedCreator, selectedCategory])

  useEffect(() => {
    if (userProfile) {
      fetchCreators()
      fetchCategories()
    }
  }, [userProfile])

  useEffect(() => {
    if (userProfile) {
      fetchNotifications()
    }
  }, [userProfile, fetchNotifications])

  // Modal açıldığında istatistikleri getir
  useEffect(() => {
    if (selectedNotification && activeTab === 'stats') {
      fetchNotificationStats(selectedNotification.id)
    }
  }, [selectedNotification, activeTab])

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    )
  }

  if (!userProfile || !hasAnyRole(userProfile, ['super_admin', 'admin'])) {
    return <Navigate to="/unauthorized" replace />
  }

  const getMonthOptions = () => {
    return [
      { value: '', label: 'Tüm Aylar' },
      { value: '1', label: 'Ocak' },
      { value: '2', label: 'Şubat' },
      { value: '3', label: 'Mart' },
      { value: '4', label: 'Nisan' },
      { value: '5', label: 'Mayıs' },
      { value: '6', label: 'Haziran' },
      { value: '7', label: 'Temmuz' },
      { value: '8', label: 'Ağustos' },
      { value: '9', label: 'Eylül' },
      { value: '10', label: 'Ekim' },
      { value: '11', label: 'Kasım' },
      { value: '12', label: 'Aralık' },
    ]
  }

  const getDayOptions = () => {
    if (!selectedMonth) return []
    const days = []
    const year = parseInt(selectedYear) || new Date().getFullYear()
    const month = parseInt(selectedMonth)
    const daysInMonth = new Date(year, month, 0).getDate()
    
    days.push({ value: '', label: 'Tüm Günler' })
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ value: i.toString(), label: i.toString() })
    }
    return days
  }

  const getYearOptions = () => {
    const years = []
    const currentYear = new Date().getFullYear()
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push({ value: i.toString(), label: i.toString() })
    }
    return years
  }

  const resetFilters = () => {
    setSelectedMonth('')
    setSelectedDay('')
    setSelectedYear(new Date().getFullYear().toString())
    setSearchTerm('')
    setSelectedCreator('')
    setSelectedCategory('')
    setCurrentPage(1)
  }

  // İlk hedef okulun durumunu al (admin için)
  const getStatusForNotification = (notification) => {
    if (!notification.targets || notification.targets.length === 0) {
      return 'Okunmadı'
    }
    // İlk okulun durumunu al
    const firstSchoolId = notification.targets[0]?.school_id
    if (!firstSchoolId) return 'Okunmadı'
    
    const status = notification.status?.find((s) => s.school_id === firstSchoolId)
    return status?.status || 'Okunmadı'
  }

  // Bildirim istatistiklerini getir
  const fetchNotificationStats = async (notificationId) => {
    try {
      setStatsLoading(true)
      
      // Bildirimin hedef okullarını getir
      const { data: targets, error: targetsError } = await supabase
        .from('notification_targets')
        .select(`
          school_id,
          school:schools(id, okul_adi)
        `)
        .eq('notification_id', notificationId)

      if (targetsError) throw targetsError

      // Her okulun durumunu getir
      const { data: statuses, error: statusError } = await supabase
        .from('notification_status')
        .select('*')
        .eq('notification_id', notificationId)

      if (statusError) throw statusError

      // Okulları ve durumlarını birleştir
      const stats = (targets || []).map(target => {
        const status = statuses?.find(s => s.school_id === target.school_id)
        return {
          school_id: target.school_id,
          school_name: target.school?.okul_adi || 'Bilinmeyen Okul',
          status: status?.status || 'Okunmadı',
          notes: status?.notes || '',
          updated_at: status?.updated_at || null,
          updated_by: status?.updated_by || null
        }
      })

      setNotificationStats(stats)
    } catch (error) {
      console.error('İstatistikler yüklenemedi:', error)
      alert('İstatistikler yüklenirken hata oluştu: ' + error.message)
    } finally {
      setStatsLoading(false)
    }
  }

  if (loading && notifications.length === 0) {
    return (
      <Layout>
        <div className="text-center py-12">Yükleniyor...</div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Bildirim Geçmişi</h1>
        <p className="text-gray-600 mt-1">Tüm bildirimleri görüntüleyin ve filtreleyin</p>
      </div>

      {/* Filtreler */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Filter size={20} />
            Filtreler
          </h2>
          <button
            onClick={resetFilters}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Filtreleri Temizle
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Yıl */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Yıl
            </label>
            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {getYearOptions().map(year => (
                <option key={year.value} value={year.value}>{year.label}</option>
              ))}
            </select>
          </div>

          {/* Ay */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ay
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value)
                setSelectedDay('')
                setCurrentPage(1)
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {getMonthOptions().map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>

          {/* Gün */}
          {selectedMonth && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gün
              </label>
              <select
                value={selectedDay}
                onChange={(e) => {
                  setSelectedDay(e.target.value)
                  setCurrentPage(1)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {getDayOptions().map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Gönderen */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gönderen
            </label>
            <select
              value={selectedCreator}
              onChange={(e) => {
                setSelectedCreator(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tüm Gönderenler</option>
              {creators.map(creator => (
                <option key={creator.id} value={creator.id}>
                  {creator.full_name || creator.email}
                </option>
              ))}
            </select>
          </div>

          {/* Kategori */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kategori
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tüm Kategoriler</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.display_name}
                </option>
              ))}
            </select>
          </div>

          {/* Arama */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Arama
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="Başlık veya açıklamada ara..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sonuçlar */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            Toplam <strong>{totalCount}</strong> bildirim bulundu
          </p>
        </div>

        {notifications.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            Bildirim bulunamadı
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {notifications.map((notification) => {
                const currentStatus = getStatusForNotification(notification)
                const statusOption = statusOptions.find((o) => o.value === currentStatus)
                
                // Son tarih kontrolü
                const isOverdue = notification.son_tarih && 
                  new Date(notification.son_tarih) < new Date() && 
                  currentStatus !== 'Yaptım'
                
                // Kart rengi - son tarih geçtiyse ve yaptım değilse kırmızı
                const cardClassName = isOverdue
                  ? 'p-6 hover:bg-red-100 transition-colors relative overflow-hidden bg-red-50'
                  : 'p-6 hover:bg-gray-50 transition-colors relative overflow-hidden'

                return (
                  <div 
                    key={notification.id} 
                    className={`${cardClassName} cursor-pointer`}
                    onClick={() => {
                      setSelectedNotification(notification)
                      setActiveTab('details')
                    }}
                  >
                    {/* Acil/Önemli Bayrak - Sol Üst Köşe */}
                    {(notification.is_urgent || notification.is_important) && (
                      <div className="absolute top-0 left-0 z-10" style={{ width: '0', height: '0' }}>
                        {/* Ana bayrak üçgeni */}
                        <div 
                          className="absolute"
                          style={{
                            width: '0',
                            height: '0',
                            borderStyle: 'solid',
                            borderWidth: '60px 60px 0 0',
                            borderColor: notification.is_urgent 
                              ? '#DC2626 transparent transparent transparent'
                              : '#EA580C transparent transparent transparent',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                          }}
                        />
                        {/* İkinci renk (hem acil hem önemli ise) */}
                        {notification.is_urgent && notification.is_important && (
                          <div 
                            className="absolute"
                            style={{
                              top: '0',
                              left: '0',
                              width: '0',
                              height: '0',
                              borderStyle: 'solid',
                              borderWidth: '30px 30px 0 0',
                              borderColor: '#EA580C transparent transparent transparent'
                            }}
                          />
                        )}
                        {/* Bayrak metni */}
                        <span 
                          className="absolute text-white text-[10px] font-bold whitespace-nowrap pointer-events-none"
                          style={{
                            top: '15px',
                            left: '10px',
                            transform: 'rotate(-45deg)',
                            transformOrigin: '0 0',
                            textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                            letterSpacing: '0.3px',
                            lineHeight: '1.2'
                          }}
                        >
                          {notification.is_urgent && notification.is_important 
                            ? 'ACİL & ÖNEMLİ' 
                            : notification.is_urgent 
                            ? 'ACİL' 
                            : 'ÖNEMLİ'}
                        </span>
                      </div>
                    )}
                    
                    {/* Kategoriler - Sağ Üst */}
                    {notification.categories && notification.categories.length > 0 && (
                      <div className="absolute top-4 right-4 flex items-center space-x-1 flex-wrap gap-1 justify-end max-w-[200px]">
                        {notification.categories.map((rel, idx) => (
                          rel.category && (
                            <span
                              key={idx}
                              className="px-2 py-1 text-xs rounded text-gray-700 border"
                              style={{ 
                                backgroundColor: `${rel.category.color || '#3B82F6'}20`,
                                borderColor: `${rel.category.color || '#3B82F6'}40`,
                                color: rel.category.color || '#3B82F6'
                              }}
                            >
                              {rel.category.display_name}
                            </span>
                          )
                        ))}
                      </div>
                    )}

                    <div className="flex justify-between items-start pr-32">
                      <div className="flex-1">
                        {/* Başlık ve Durum */}
                        <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
                          <h3 className={`text-lg font-semibold ${isOverdue ? 'text-red-900' : 'text-gray-900'} ${(notification.is_urgent || notification.is_important) ? 'pl-14' : ''}`}>
                            {notification.title}
                          </h3>
                          <span
                            className={`px-2 py-1 text-xs rounded font-medium ${
                              statusOption
                                ? statusOption.color === 'blue' ? 'bg-blue-100 text-blue-800'
                                : statusOption.color === 'yellow' ? 'bg-yellow-100 text-yellow-800'
                                : statusOption.color === 'green' ? 'bg-green-100 text-green-800'
                                : statusOption.color === 'red' ? 'bg-red-100 text-red-800'
                                : statusOption.color === 'orange' ? 'bg-orange-100 text-orange-800'
                                : 'bg-gray-100 text-gray-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {currentStatus}
                          </span>
                        </div>
                        {notification.description && (
                          <p className={`mb-2 line-clamp-2 ${isOverdue ? 'text-red-700' : 'text-gray-600'}`}>
                            {notification.description}
                          </p>
                        )}
                        <div className={`flex items-center gap-4 text-sm flex-wrap ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
                          <div className="flex items-center gap-1">
                            <Calendar size={16} />
                            <span>
                              {new Date(notification.created_at).toLocaleString('tr-TR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {notification.created_by_user && (
                            <div className="flex items-center gap-1">
                              <User size={16} />
                              <span>
                                {notification.created_by_user.full_name || notification.created_by_user.email}
                              </span>
                            </div>
                          )}
                          {notification.targets && notification.targets.length > 0 && (
                            <div className="flex items-center gap-1">
                              <School size={16} />
                              <span>
                                {notification.targets.length} okula gönderildi
                              </span>
                            </div>
                          )}
                          {notification.son_tarih && (
                            <span className={isOverdue ? 'font-semibold' : ''}>
                              Son Tarih:{' '}
                              {new Date(notification.son_tarih).toLocaleDateString('tr-TR')}
                              {isOverdue && ' ⚠️ (Geçti!)'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Gönderilen Okullar */}
                    {notification.targets && notification.targets.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className={`text-xs font-medium mb-2 ${isOverdue ? 'text-red-700' : 'text-gray-700'}`}>Gönderilen Okullar:</p>
                        <div className="flex flex-wrap gap-2">
                          {notification.targets.slice(0, 10).map((target, idx) => (
                            target.school && (
                              <span
                                key={idx}
                                className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800"
                              >
                                {target.school.okul_adi}
                              </span>
                            )
                          ))}
                          {notification.targets.length > 10 && (
                            <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600">
                              +{notification.targets.length - 10} daha
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Sayfalama */}
            {totalPages > 1 && (
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Sayfa {currentPage} / {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <ChevronLeft size={18} />
                    Önceki
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    Sonraki
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detay ve İstatistik Modal */}
      {selectedNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedNotification.title}
                </h2>
                <button
                  onClick={() => {
                    setSelectedNotification(null)
                    setActiveTab('details')
                    setStatsStatusFilter('all')
                    setStatsSearchTerm('')
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Tab Menüsü */}
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === 'details'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Detaylar
                </button>
                <button
                  onClick={() => {
                    setActiveTab('stats')
                    if (selectedNotification) {
                      fetchNotificationStats(selectedNotification.id)
                    }
                  }}
                  className={`px-4 py-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                    activeTab === 'stats'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <BarChart3 size={18} />
                  İstatistikler
                </button>
              </div>

              {/* Detaylar Sekmesi */}
              {activeTab === 'details' && (
                <div>
                  {selectedNotification.description && (
                    <div className="mb-4">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {selectedNotification.description}
                      </p>
                    </div>
                  )}

                  <div className="mb-4 text-sm text-gray-500">
                    <p>
                      Oluşturulma: {new Date(selectedNotification.created_at).toLocaleString('tr-TR')}
                    </p>
                    {selectedNotification.son_tarih && (
                      <p>
                        Son Tarih: {new Date(selectedNotification.son_tarih).toLocaleDateString('tr-TR')}
                      </p>
                    )}
                  </div>

                  {selectedNotification.targets && selectedNotification.targets.length > 0 && (
                    <div className="mb-4">
                      <h3 className="font-semibold mb-2">Gönderilen Okullar</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedNotification.targets.map((target, idx) => (
                          target.school && (
                            <span
                              key={idx}
                              className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800"
                            >
                              {target.school.okul_adi}
                            </span>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              )}

              {/* İstatistikler Sekmesi */}
              {activeTab === 'stats' && (
                <div>
                  {statsLoading ? (
                    <div className="text-center py-12">
                      <div className="text-lg">Yükleniyor...</div>
                    </div>
                  ) : (
                    <>
                      {/* İstatistik Özeti */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-blue-600">
                            {notificationStats.length}
                          </div>
                          <div className="text-sm text-blue-700">Toplam Okul</div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-green-600">
                            {notificationStats.filter(s => s.status === 'Yaptım').length}
                          </div>
                          <div className="text-sm text-green-700">Yaptı</div>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-red-600">
                            {notificationStats.filter(s => s.status === 'Yapmadım').length}
                          </div>
                          <div className="text-sm text-red-700">Yapmadı</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-gray-600">
                            {notificationStats.filter(s => s.status === 'Okunmadı').length}
                          </div>
                          <div className="text-sm text-gray-700">Okunmadı</div>
                        </div>
                      </div>

                      {/* Filtreler */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="flex flex-col md:flex-row gap-4">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Durum Filtresi
                            </label>
                            <select
                              value={statsStatusFilter}
                              onChange={(e) => setStatsStatusFilter(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="all">Tüm Durumlar</option>
                              {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Okul Ara
                            </label>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                              <input
                                type="text"
                                value={statsSearchTerm}
                                onChange={(e) => setStatsSearchTerm(e.target.value)}
                                placeholder="Okul adı ile ara..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Okul Listesi */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                          <h3 className="font-semibold text-gray-900">Okul Durumları</h3>
                        </div>
                        <div className="max-h-[400px] overflow-y-auto">
                          {(() => {
                            // Filtreleme
                            let filteredStats = notificationStats
                            
                            if (statsStatusFilter !== 'all') {
                              filteredStats = filteredStats.filter(s => s.status === statsStatusFilter)
                            }
                            
                            if (statsSearchTerm) {
                              filteredStats = filteredStats.filter(s =>
                                s.school_name.toLowerCase().includes(statsSearchTerm.toLowerCase())
                              )
                            }

                            if (filteredStats.length === 0) {
                              return (
                                <div className="p-8 text-center text-gray-500">
                                  Sonuç bulunamadı
                                </div>
                              )
                            }

                            return (
                              <table className="w-full">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Okul Adı
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Durum
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Güncelleme Tarihi
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Notlar
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {filteredStats.map((stat, idx) => {
                                    const statusOption = statusOptions.find(o => o.value === stat.status)
                                    return (
                                      <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                          {stat.school_name}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <span
                                            className={`px-2 py-1 text-xs rounded font-medium ${
                                              statusOption
                                                ? statusOption.color === 'blue' ? 'bg-blue-100 text-blue-800'
                                                : statusOption.color === 'yellow' ? 'bg-yellow-100 text-yellow-800'
                                                : statusOption.color === 'green' ? 'bg-green-100 text-green-800'
                                                : statusOption.color === 'red' ? 'bg-red-100 text-red-800'
                                                : statusOption.color === 'orange' ? 'bg-orange-100 text-orange-800'
                                                : 'bg-gray-100 text-gray-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }`}
                                          >
                                            {stat.status}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                          {stat.updated_at
                                            ? new Date(stat.updated_at).toLocaleString('tr-TR')
                                            : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                          {stat.notes || '-'}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            )
                          })()}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default NotificationHistory

